'use client';

import { useState } from 'react';

import {
  MAX_IMAGE_UPLOAD_BYTES,
  MAX_IMAGE_UPLOAD_MEGABYTES,
} from '@opositandopn/http';

import { readImageFileAsDataUrl } from '../../../../../utils/files';
import { Meter } from '../../../../meter';
import { ResourceBody } from '../../../../resource';
import { useToasterActions } from '../../../../toaster';
import { SubmitBar } from '../../../layout/SubmitBar';
import { TitleBar } from '../../../layout/TitleBar';
import type { MultipleChoiceQuestionType } from '../../../multiple-choice-question/MultipleChoiceQuestion.types';
import type { ScannedAnswer } from '../../MultipleChoiceAssessment.types';
import { AnswerSheetUpload } from '../answer-sheet-upload/AnswerSheetUpload';
import { AssessmentAnswersForm } from '../assessment-answers-form/AssessmentAnswersForm';

import styles from './Steps.module.scss';

type AnswersStepProps = {
  questions: MultipleChoiceQuestionType[];
  isSubmitting: boolean;
  onSelect: (id: string, answerIndex: number) => void;
  onSubmit: () => void;
  /** Offered only when the consumer can turn a photo of the sheet into answers. */
  onScanAnswerSheet?: (base64Image: string) => Promise<ScannedAnswer[]>;
  /** Throws the attempt away, as the alternative to leaving it for later. */
  onDiscard?: () => void;
  onClose?: () => void;
};

/**
 * Transcribing what was answered on paper. The sitting is over by the time this shows,
 * so there is no clock and no way back to the questions.
 */
function AnswersStep({
  questions,
  isSubmitting,
  onSelect,
  onSubmit,
  onScanAnswerSheet,
  onDiscard,
  onClose,
}: AnswersStepProps) {
  const [isScanning, setIsScanning] = useState(false);
  // What the last scan could not settle on its own. Held here rather than on the
  // questions because it is a fact about the photograph, not about the assessment —
  // it dies with the step and never reaches whoever owns the answers.
  const [questionsRequiringReview, setQuestionsRequiringReview] = useState<
    string[]
  >([]);
  const { sendMessage } = useToasterActions();

  const answeredCount = questions.filter(
    (question) => question.selectedOptionIndex > 0,
  ).length;

  // Every scan speaks for the whole sheet, so what a previous photo left behind is not
  // an answer any more — a question the new reading does not report is a question that
  // photo shows blank, and leaving the old mark standing would file it as read.
  const clearAnswers = () => {
    for (const question of questions) {
      if (question.selectedOptionIndex > 0) {
        onSelect(question.id, 0);
      }
    }

    setQuestionsRequiringReview([]);
  };

  // The photo travels exactly as the camera took it — no resizing and no re-encoding,
  // as the scan reads faint pencil marks that compression is quick to smudge away.
  const scanAnswerSheet = async (photo: File) => {
    if (!onScanAnswerSheet) {
      return;
    }

    // Turned away here rather than by the API: the photo is the size it is, and a phone on
    // mobile data would spend a minute uploading one that comes back refused.
    if (photo.size > MAX_IMAGE_UPLOAD_BYTES) {
      sendMessage(
        '¡Ups!',
        `La foto no puede superar los ${MAX_IMAGE_UPLOAD_MEGABYTES}MB. Hazla de nuevo con menos resolución.`,
        'danger',
      );

      return;
    }

    setIsScanning(true);

    try {
      let photoDataUrl: string;

      try {
        photoDataUrl = await readImageFileAsDataUrl(photo);
      } catch {
        // A read that fails here never reaches the consumer, so without this the pick
        // is a no-op the user gets no explanation for — which is exactly how a photo
        // in a format we cannot send used to behave.
        sendMessage(
          '¡Ups!',
          'No hemos podido leer esa foto. Hazla de nuevo con la cámara o elige otra.',
          'danger',
        );

        return;
      }

      const scanned = await onScanAnswerSheet(photoDataUrl);

      // Emptied once the reading is in hand rather than before it is asked for. It has
      // to happen: this photo speaks for the whole sheet, so a question it shows blank
      // must not keep the mark an earlier one left. But doing it up front meant every
      // refused photo — out of focus, too compressed, any of them — wiped answers the
      // user had typed in by hand, with nothing to undo it.
      clearAnswers();

      const flagged: string[] = [];

      for (const { number, answerIndex, requiresReview } of scanned) {
        const question = questions[number - 1];

        if (!question) {
          continue;
        }

        if (answerIndex > 0 && answerIndex <= question.options.length) {
          onSelect(question.id, answerIndex);
        }

        // Flagged whatever the reader ended up writing into the form, which since the
        // reader's 0.6.0 is no longer always a blank: a mark too faint to be sure of
        // now arrives with its letter filled in, while two marks left standing still
        // come back empty. Either way the row is the user's to settle — the difference
        // is only whether they are confirming a reading or supplying one.
        if (requiresReview) {
          flagged.push(question.id);
        }
      }

      setQuestionsRequiringReview(flagged);

      if (flagged.length > 0) {
        // Amber choices on their own do not say why, and the rows needing a second look
        // can sit anywhere down a sheet of a hundred questions.
        sendMessage(
          'Revisa tus respuestas',
          flagged.length === 1
            ? 'No hemos podido leer una pregunta con seguridad. La tienes marcada en amarillo.'
            : `No hemos podido leer ${flagged.length} preguntas con seguridad. Las tienes marcadas en amarillo.`,
          'warning',
        );
      }
    } catch {
      // The form is left exactly as it was. A photo the API refuses is one to retake,
      // not a reason to throw away what is already transcribed. The consumer reports
      // the failure — the API says which thing about the photo was wrong.
    } finally {
      setIsScanning(false);
    }
  };

  const isBusy = isSubmitting || isScanning;

  return (
    <>
      <TitleBar
        title="Rellena tus respuestas"
        actions={
          onScanAnswerSheet && (
            <AnswerSheetUpload
              disabled={isSubmitting}
              loading={isScanning}
              onSelect={scanAnswerSheet}
            />
          )
        }
        close={
          onClose && {
            closeConfirmation: true,
            confirmationMessage: '¿Estás segurx de que deseas salir?',
            confirmationSubtitle:
              'Las respuestas que hayas trasladado se perderán, pero podrás subirlas más tarde',
            onDiscard,
            onClose,
          }
        }
      />
      <ResourceBody spacious={true}>
        <AssessmentAnswersForm
          questions={questions}
          disabled={isBusy}
          questionsRequiringReview={questionsRequiringReview}
          onSelect={(id, answerIndex) => {
            // Touching a flagged question is the review — whatever the user picks is
            // their answer, so the row goes back to its usual colours.
            setQuestionsRequiringReview((flagged) =>
              flagged.filter((questionId) => questionId !== id),
            );

            onSelect(id, answerIndex);
          }}
        />
      </ResourceBody>
      <SubmitBar
        isSubmitting={isSubmitting}
        // A scan in flight only holds the button: the spinner is the sending's, and
        // showing it for a photo being read says the attempt is on its way when it is
        // not. Sending while a row the scan could not read is still flagged would file
        // a guess as the user's answer, so the review has to be finished first.
        // Reaching the end of one no longer costs an answer the candidate never gave:
        // a flagged row deselects like any other, so clicking an option and clicking it
        // again settles it as blank, which is what the reader was unsure about half the
        // time.
        disabled={isScanning || questionsRequiringReview.length > 0}
        onSubmit={onSubmit}
      >
        <div className={styles['footer-slot']}>
          <Meter
            className={styles['progress']}
            // Amber while a row the scan could not read is still waiting on the user,
            // so the bar carries the same warning the flagged rows do; green once
            // nothing is left to review.
            variant={
              questionsRequiringReview.length > 0 ? 'warning' : 'success'
            }
            value={
              questions.length > 0
                ? (answeredCount / questions.length) * 100
                : 0
            }
            label="Preguntas respondidas"
          />
          <span className={styles['counter']}>
            {answeredCount} de {questions.length}
          </span>
        </div>
      </SubmitBar>
    </>
  );
}

export { AnswersStep };
